/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { BookmarkService, createBookmarkService } from 'fl-bookmarks';
import {
  ContentAuthorizationConfiguration,
  ContentAuthorizationData,
  ContentAuthorizer,
  createContentAuthorizer,
} from 'fl-content-authorization';
import { FavouriteService, createFavouriteService } from 'fl-favourites';
import { Error, isError } from 'fl-foundation';
import { HeartbeatBuilder, HeartbeatConfiguration, createHeartbeatBuilder } from 'fl-heartbeat';
import {
  AuthorizationData,
  PlatformAuthorizer,
  PlatformClient,
  PlatformConfig,
  UserAuthorizationData,
  UserAuthorizationDelegate,
  createPlatformAuthorizer,
  createPlatformClient,
} from 'fl-platform-authorizer';
import { createComposablePlayer } from 'fl-player';
import { ComposablePlayer, DrmScheme, Player } from 'fl-player-interface';
import { ResourceVm } from 'qp-ui-interface';
import { getAppVersion, isFreeContent } from 'qp-utility';
import { LocalStorageKeys } from '../../LocalStorageKeys';
import { GetPlatformPlayerModel } from '../../interfaces/player.interface';
import { CatchupInfo } from '../../models/epgData';
import { ErrorFactory } from '../../modules/errors/handler/ErrorFactory';
import { isLoggedIn } from '../../utils/LoginUtils';
import {
  createIMADAIPlayer,
  getAdStreamMetadata,
  getAdTags,
  getCustParams,
  getHeaders,
  getPlatformAsset,
  getPlatformPlayer,
  handleLicenseRequest,
  isGoogleIMAUndefined,
} from '../../utils/PlaybackUtils';
import { getXClientId } from '../../utils/deviceUtils';
import { isLiveByMetadata, isResourceBookmark } from '../../utils/playerUtils';
import { saveAuthDataToken } from '../../utils/tokenUtils';
import { AdobeService } from '../adobe/adobe.service';
import { AppConfig, ConfigService } from '../config/config.service';
import { DeviceHandlerService } from '../device-handler/device-handler.service';
import { FireboltService } from '../firebolt.service';
import { TokenService } from '../firstlight/guest-token/token.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { QPATService } from '../qpat/qpat.service';
import { ScreenService } from '../screen/screen.service';

@Injectable({
  providedIn: 'root',
})
export class PlaybackService {
  contentAuthorizer?: ContentAuthorizer;
  platformAuthorizer?: PlatformAuthorizer;
  bookmarkService?: BookmarkService;
  favoriteService?: FavouriteService;
  ovatToken: string;
  widevineCertificate!: ArrayBuffer;
  fairPlayCertificate!: ArrayBuffer;
  thumbnailSupported = false;
  composablePlayer: ComposablePlayer;
  private contentAuthConfiguration?: ContentAuthorizationConfiguration;
  private platformConfig?: PlatformConfig;
  private startTimePlayback = 0;

  constructor(
    private configService: ConfigService,
    private deviceHandlerService: DeviceHandlerService,
    private tokenService: TokenService,
    private qpatService: QPATService,
    private localStorage: LocalStorageService,
    private adobeService: AdobeService,
    private screenService: ScreenService,
    private fireboltService: FireboltService
  ) {}

  set startTime(startTime: number) {
    this.startTimePlayback = startTime;
  }

  get startTime(): number {
    return this.startTimePlayback;
  }

  /**
   * set plarform config and content auth config
   *
   * @param applicationConfig
   */
  public intializeConfigurations(applicationConfig?: AppConfig): void {
    const appConfiguration = applicationConfig ? applicationConfig : this.configService.applicationConfig;
    this.platformConfig = {
      clientID: appConfiguration.clientID,
      clientSecret: appConfiguration.clientSecret,
      xClientId: getXClientId(),
      authorizationEndPoint: appConfiguration.oAuthURL,
    };
    this.contentAuthConfiguration = {
      endPointUrl: appConfiguration.contentAuthURL,
      clientRegistrationEndPointUrl: appConfiguration.clientRegURL,
    };
  }

  /**
   * Init SDK services
   * create platform authorizer
   * ensure authorization
   * save authdata in localstorage
   * init bookmarks, content authorizer, favorite service
   * set widevine certificate
   *
   * @returns Promise<AuthorizationData | Error>
   */
  async initialiseSDK(): Promise<AuthorizationData | Error> {
    this.intializeConfigurations();
    if (!this.platformAuthorizer) {
      this.createPlatformAuthoriser();
    }
    const authData: AuthorizationData | Error = await this.ensureAuthorization();
    if (!isError(authData)) saveAuthDataToken(authData, this.localStorage);
    this.initialiseOtherSDKServices();
    await this.fetchWVCertificate();
    return Promise.resolve(authData);
  }

  async validateAuthToken(): Promise<AuthorizationData | Error> {
    this.intializeConfigurations();
    if (!this.platformAuthorizer) {
      this.createPlatformAuthoriser();
    }
    const authData: AuthorizationData | Error = await this.ensureAuthorization();
    if (!isError(authData)) saveAuthDataToken(authData, this.localStorage);
    return Promise.resolve(authData);
  }

  /**
   * Fetch the widevine certificate
   *
   * @returns
   */
  async fetchWVCertificate(): Promise<ArrayBuffer> {
    if (this.widevineCertificate) return this.widevineCertificate;
    const widevineUrl =
      this.configService.applicationConfig?.widevineCertBinUrl ||
      'https://storage.googleapis.com/wvmedia/cert/cert_license_widevine_com_prod.bin';
    const request = await fetch(widevineUrl);
    this.widevineCertificate = await request.arrayBuffer();
    return this.widevineCertificate;
  }

  /**
   * Fetch the fair play certificate
   *
   * @param url
   * @returns
   */
  async fetchFPCertificate(url: string): Promise<ArrayBuffer> {
    if (this.fairPlayCertificate) return this.fairPlayCertificate;
    const request = await fetch(url);
    this.fairPlayCertificate = await request.arrayBuffer();
    return this.fairPlayCertificate;
  }

  async getCertificate(fpCertificateUrl?: string, drmScheme?: DrmScheme) {
    if (drmScheme === DrmScheme.FAIRPLAY) {
      return this.fetchFPCertificate(fpCertificateUrl);
    } else if (drmScheme === DrmScheme.WIDEVINE) {
      return this.fetchWVCertificate();
    } else {
      return undefined;
    }
  }

  /**
   * Call ensure authorization
   *
   * @returns Promise<AuthorizationData | Error>
   */
  async ensureAuthorization() {
    const authData: AuthorizationData | Error = await this.platformAuthorizer.ensureAuthorization();
    if (!isError(authData)) {
      saveAuthDataToken(authData, this.localStorage);
    }
    return authData;
  }

  /**
   * Authorize playback by content id and user auth
   * Call to createPlayerInstance
   */
  public async loadPlayerInstance(
    videoElement: HTMLVideoElement,
    adContainerElement: HTMLDivElement,
    uniqueId: string,
    metadataType: string,
    bookmarkcontentId: string = undefined,
    loadThumbnails = false,
    loadAds: boolean = false,
    resource: ResourceVm,
    catchupInfo?: CatchupInfo
  ): Promise<{ contentAuthorizationData: ContentAuthorizationData; playerInstance: Player }> {
    const platformAsset = getPlatformAsset(uniqueId, metadataType, catchupInfo, this.configService.applicationConfig);
    let shortMediaToken: string;
    if (!isFreeContent(resource)) {
      const entitlementPackages = resource.entitlement[0]?.subscriptionPackage ?? [];
      await this.adobeService.userIsEntitled(entitlementPackages);
      shortMediaToken = this.adobeService.mediaToken;
    }
    const contentAuthorizationData = await this.contentAuthorizer.authorizePlayback(platformAsset, getHeaders(shortMediaToken));
    loadAds = loadAds && contentAuthorizationData?.ssaiEnabled;
    const seekPos = bookmarkcontentId ? await this.getSeekPosition(metadataType, uniqueId, bookmarkcontentId) : 0;
    const playerInstance = await this.createPlayer(
      videoElement,
      adContainerElement,
      contentAuthorizationData,
      resource,
      this.screenService.currentScreen,
      loadThumbnails,
      isLiveByMetadata(metadataType),
      !!catchupInfo,
      loadAds,
      seekPos
    );
    this.buildHeartbeat(contentAuthorizationData, isLiveByMetadata(metadataType), playerInstance);
    return { contentAuthorizationData, playerInstance };
  }

  /**
   * Create player object
   *
   * @param playerConfig
   * @param contentAuthorizationData
   * @param checkforThumbnails
   * @param seekPos
   * @returns
   */
  private async createPlayer(
    videoElement: HTMLVideoElement,
    adContainer: HTMLDivElement,
    contentAuthorizationData: ContentAuthorizationData,
    resource: ResourceVm,
    currentPage: string,
    checkforThumbnails: boolean,
    isLive: boolean,
    isCatchUp: boolean,
    loadAds: boolean,
    seekPos: number = 0
  ): Promise<Player> {
    this.thumbnailSupported = checkforThumbnails ? this.thumbnailsValidation() : false;
    const ssaiEnabled = contentAuthorizationData.ssaiEnabled;
    if (ssaiEnabled && loadAds) {
      console.log('[AD] ads enabled');
      if (isGoogleIMAUndefined()) {
        const errorCode = this.configService.applicationConfig.adErrorCode || 40050117;
        throw ErrorFactory.get({ errorCode, errorMessage: 'google not defined' }, 'ADS');
      }
      const adTags = await getAdTags(
        contentAuthorizationData.custom,
        this.localStorage,
        isLive,
        isCatchUp,
        resource,
        currentPage,
        this.adobeService.getUserInfo(),
        this.deviceHandlerService.getCurrentDeviceID(),
        videoElement.clientWidth,
        videoElement.clientHeight,
        this.configService.applicationConfig,
        // awaits the launch-intent processing, so a deeplink-delivered signal
        // cannot be missed by an ad call racing the capture
        await this.fireboltService.getAgePolicy()
      );
      console.log('[AD] adtags', adTags);
      const adMetadata = await getAdStreamMetadata(
        videoElement,
        adContainer,
        contentAuthorizationData.mediaType,
        isLive,
        adTags,
        contentAuthorizationData.contentUrl,
        contentAuthorizationData.cmsID,
        contentAuthorizationData.contentId,
        contentAuthorizationData.assetKey
      );
      // replace stream url
      if (adMetadata) {
        contentAuthorizationData.contentUrl = adMetadata.streamUrl;
      }
      const getplatformPlayerModel: GetPlatformPlayerModel = {
        seekPos,
        applicationConfig: this.configService.applicationConfig,
        isLive,
        contentAuthorizationData,
        video: videoElement,
        certificate: await this.getCertificate(contentAuthorizationData.fpCertificateUrl, contentAuthorizationData.drmScheme),
        showThumbnails: this.thumbnailSupported,
        thumbnailsUrl: undefined,
        resourceId: contentAuthorizationData.contentId,
        appVersion: getAppVersion(),
        streamUrl: adMetadata.streamUrl,
      };
      let playerInstance = await getPlatformPlayer(getplatformPlayerModel);
      if (adMetadata) {
        playerInstance = createIMADAIPlayer(adMetadata, playerInstance, videoElement);
      }
      return handleLicenseRequest(this.platformAuthorizer, playerInstance);
    }
    // base player
    const playerInstance = await getPlatformPlayer({
      seekPos,
      applicationConfig: this.configService.applicationConfig,
      isLive,
      contentAuthorizationData,
      video: videoElement,
      certificate: await this.getCertificate(contentAuthorizationData.fpCertificateUrl, contentAuthorizationData.drmScheme),
      showThumbnails: this.thumbnailSupported,
      thumbnailsUrl: undefined,
      appVersion: getAppVersion(),
      resourceId: contentAuthorizationData.contentId,
    });
    return handleLicenseRequest(this.platformAuthorizer, playerInstance);
  }

  public getCustParams(
    custParamsString: string,
    resource: ResourceVm,
    navigationSection: string = 'NA',
    clientWidth: number,
    clientHeight: number
  ) {
    return getCustParams(
      custParamsString,
      resource,
      this.adobeService.getUserInfo().mvpdHash,
      navigationSection,
      clientWidth,
      clientHeight
    );
  }

  /**
   * Check if the user is logged
   * If the user is logged, force the refresh call to qpat
   * if the user is not logged, force the refresh call to guest token
   *
   */
  public async refreshxAuthToken() {
    try {
      if (isLoggedIn(this.localStorage)) {
        this.localStorage.removeItem(LocalStorageKeys.XAUTH_TOKEN_EXP); //force to refresh
        await this.qpatService.refreshXAuthToken();
      } else {
        // guest token
        this.resetPlayback();
        await this.initialiseSDK();
        await this.contentAuthorizer.registerDevice();
      }
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Validate if thumbnails are supported
   *
   * @returns
   */
  private thumbnailsValidation(): boolean {
    return !!this.configService?.applicationConfig?.enableKeyframes;
  }

  /**
   * Resets the playback by invalidating the token
   * */
  public resetPlayback(): void {
    if (this.platformAuthorizer) {
      this.platformAuthorizer.dispose();
    }
    if (this.contentAuthorizer) {
      this.contentAuthorizer.invalidateDeviceToken();
    }
  }

  async getAccessToken(): Promise<UserAuthorizationData | Error> {
    const ovatToken = this.localStorage.getItem(LocalStorageKeys.XAUTH_TOKEN);
    if (ovatToken === null || ovatToken === '') {
      if (!isLoggedIn(this.localStorage)) {
        const authResponse: AuthorizationData | Error = await this.platformAuthorizer.ensureAuthorization();
        if (!isError(authResponse)) {
          saveAuthDataToken(authResponse, this.localStorage);
          const result = await this.tokenService.fetchGuestToken(
            this.deviceHandlerService.getCurrentDeviceID(),
            this.configService.applicationConfig.deviceType || 'web'
          );
          return { accessToken: result.token };
        }
        return authResponse;
      } else {
        const result = await this.qpatService.getQPAT();
        return { accessToken: result.token };
      }
    }
    return { accessToken: ovatToken };
  }

  private getAuthDelegate(): UserAuthorizationDelegate {
    const userAuthorizationDelegate: UserAuthorizationDelegate = {
      fetchUserAuthorizationToken: () => {
        return this.getAccessToken();
      },
    };
    return userAuthorizationDelegate;
  }

  private getPlatformClient(): PlatformClient {
    return createPlatformClient(this.deviceHandlerService.getCurrentDeviceID(), this.configService.applicationConfig.deviceType || 'web');
  }

  private createPlatformAuthoriser(): void {
    this.platformAuthorizer = createPlatformAuthorizer(this.platformConfig, this.getAuthDelegate());
  }

  private initialiseOtherSDKServices(): void {
    this.bookmarkService = createBookmarkService(this.configService.applicationConfig.bookmarkURL, this.platformAuthorizer);
    this.contentAuthorizer = createContentAuthorizer(this.contentAuthConfiguration, this.platformAuthorizer, this.getPlatformClient());
    this.favoriteService = createFavouriteService(this.configService.applicationConfig.favoriteURL, this.platformAuthorizer);
  }

  /**
   * Check if the composable player exists
   * if exist, return the insance
   * if not exist, return a new instance
   *
   * @returns Composable player
   */
  public getComposablePlayer(playerInstance: Player): ComposablePlayer {
    if (this.composablePlayer) {
      return this.composablePlayer;
    }
    this.composablePlayer = createComposablePlayer(playerInstance);
    return this.composablePlayer;
  }

  public stopComposablePlayer(): void {
    if (this.composablePlayer) {
      this.composablePlayer = undefined;
    }
  }

  /**
   * Build hearbeat and attach to player
   *
   * @param authorizationResponse
   * @returns
   */
  public buildHeartbeat(authorizationResponse: ContentAuthorizationData, isLive: boolean, playerInstance: Player) {
    // Doesn't attach Heartbeat by authorization response flag
    if (!authorizationResponse.heartbeatFlag) {
      return;
    }
    // Heartbeat config
    const heartbeatConfiguration: HeartbeatConfiguration = {
      heartbeatEndPointUrl: this.configService.applicationConfig.heartBeatURL,
      syncIntervalMS: parseInt(this.configService.applicationConfig.heartBeatSyncInterval, 10),
    };

    // Building Heartbeat
    const heartbeatBuilder: HeartbeatBuilder = createHeartbeatBuilder(
      this.deviceHandlerService.getCurrentDeviceID(),
      authorizationResponse.contentId,
      heartbeatConfiguration,
      this.platformAuthorizer
    );
    const hBuilder = heartbeatBuilder.setHeartbeatToken(authorizationResponse.heartbeatToken);
    if (isLive) {
      hBuilder
        .setLiveEndTime(authorizationResponse.liveEndTime)
        .setLiveStartTime(authorizationResponse.liveStartTime)
        .setProgramEndTime(authorizationResponse.pgmEndTime)
        .setProgramStartTime(authorizationResponse.pgmStartTime)
        .setProgramBlackout(authorizationResponse.pgmBlackout ?? false);
    }
    const heartbeatManager = hBuilder.build();
    // Attaches heartbeat to the player
    if (this.composablePlayer) {
      this.composablePlayer.addStateChangeStep((player: Player) => heartbeatManager.processPlayerStateChange(player));
      this.composablePlayer.addHeartBeatStep((player: Player) => heartbeatManager.processHeartbeatChange(player));
    } else {
      this.composablePlayer = this.getComposablePlayer(playerInstance);
      this.composablePlayer.addStateChangeStep((player: Player) => heartbeatManager.processPlayerStateChange(player));
      this.composablePlayer.addHeartBeatStep((player: Player) => heartbeatManager.processHeartbeatChange(player));
    }
  }

  /**
   * Get seek position by bookmark
   *
   * @returns seek position
   */
  private async getSeekPosition(contentType: string, uniqueId: string, bookmarkcontentId: string): Promise<number> {
    try {
      if (isLoggedIn(this.localStorage)) {
        let currBookmarkResponse;
        if (contentType.includes('series') || contentType.includes('episode')) {
          const episodeBookmarks = await this.bookmarkService.getSeriesBookmarks(bookmarkcontentId);
          currBookmarkResponse = episodeBookmarks.find((bookmark) => bookmark.episodeId === uniqueId);
        } else {
          currBookmarkResponse = await this.bookmarkService.getBookmark(bookmarkcontentId);
        }
        if (isResourceBookmark(contentType, uniqueId, currBookmarkResponse)) {
          const seekPos = currBookmarkResponse.offset ? currBookmarkResponse.offset / 1000 : 0;
          return seekPos || 0;
        }
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }
}
